import { useSelector } from 'react-redux'
import Item from './Item'
import props from '../../../../../redux/props'
import { Concept, ConceptDataCluster } from '../../../../../types/Survey/Analysis/Concept'
import _ from 'lodash'
import getSums from '../ConceptCalculation/OrganizationUnit/SurveyResults/getSums'
import { ResultPropsAnwesenheit } from '../../../../../types/Survey/Analysis/FactorValues'
import { ProductionDistance } from '../../../../../types/OrganizationUnit'
import { isUnitAllowedByProductionDistance } from './SummaryProductionDistance'
import { isUnitAllowedByFloor } from './SummaryFloor'
import TableItem, { TableItemRow } from './TableItem'

type Props = { concept: Concept; conceptDataCluster: ConceptDataCluster; productionDistance?: ProductionDistance; floorUUID?: string }

const General: React.FC<Props> = ({ concept, conceptDataCluster, productionDistance, floorUUID }) => {
  const t = useSelector((s) => s[props.TRANSLATION])
  const sums = getSums(
    productionDistance
      ? concept.units.sum.productionDistance[productionDistance]
      : floorUUID
      ? concept.units.sum.floor[floorUUID]
      : concept.units.sum,
  )

  const calculateAnwesenheit = (): number => {
    const anwesenheit: ResultPropsAnwesenheit = sums.source.anwesenheit
    const weightedAverages = anwesenheit.averages.map((average, index) => {
      return average * anwesenheit.answerAmounts[index]
    })
    return _.sum(weightedAverages) / _.sum(anwesenheit.answerAmounts)
  }

  const getAreaTotalIst = (): number => {
    if (floorUUID) {
      return conceptDataCluster.teamSurvey.context.floors.find((f) => f.uuid === floorUUID)?.squareMeter
    } else {
      const areaTotalIst = conceptDataCluster.teamSurvey.context.floors.reduce((acc, floor) => {
        return acc + floor.squareMeter
      }, 0)
      return areaTotalIst
    }
  }

  const getUnitAmount = (): number => {
    return Object.keys(concept.units)
      .filter((u) => u !== 'sum')
      .filter((u) => isUnitAllowedByProductionDistance(concept, u, productionDistance))
      .filter((u) => isUnitAllowedByFloor(concept, u, floorUUID)).length
  }

  return (
    <div>
      <div className="border-b border-blue font-bold">{t.survey.analysis.concept.summary.general.title}</div>
      <Item title={t.survey.analysis.concept.summary.general.areaTotal} value={sums.source.total} suffix={t.generic.squareMeterSuffix} doRound />
      <Item title={t.survey.analysis.concept.summary.general.areaTotalIst} value={getAreaTotalIst()} suffix={t.generic.squareMeterSuffix} doRound />
      <Item
        title={t.survey.analysis.concept.summary.general.areaDifference}
        value={getAreaTotalIst() - sums.source.total}
        suffix={t.generic.squareMeterSuffix}
        doRound
      />
      <Item
        title={t.survey.analysis.concept.summary.general.squareMeterPerMitarbeiter}
        value={sums.squareMeterPerMitarbeiter}
        suffix={t.survey.analysis.concept.summary.general.squareMeterPerMitarbeiterSuffix}
      />
      <Item title={t.survey.analysis.concept.summary.general.employeeAmount} value={sums.source.mitarbeiter.amount} />
      <TableItem
        title={t.survey.analysis.concept.summary.general.organizationUnitAmount}
        value={getUnitAmount()}
        doRound={false}
        suffix={t.generic.employeeSuffix}
        items={Object.keys(concept.units)
          .filter((u) => u !== 'sum')
          .filter((u) => isUnitAllowedByProductionDistance(concept, u, productionDistance))
          .filter((u) => isUnitAllowedByFloor(concept, u, floorUUID))
          .map((u) => (
            <TableItemRow
              key={u}
              title={concept.units[u].name}
              values={[
                {
                  value: concept.units[u].mitarbeiter,
                  suffix: t.generic.employeeSuffix,
                },
              ]}
            />
          ))}
      />
      <Item
        title={t.survey.analysis.concept.summary.general.anwesenheitAverage}
        value={calculateAnwesenheit()}
        doRound={false}
        suffix={t.generic.percentageSuffix}
      />
      <Item
        title={t.survey.analysis.concept.summary.general.deskAmount}
        value={sums.source.schreibtische.practicalAmount}
        suffix={t.generic.amountSuffix}
      />
      <Item
        title={t.survey.analysis.concept.summary.general.desksharingQuoteAverage}
        value={_.sum(sums.source.desksharingPercentages.percentages) / sums.source.desksharingPercentages.percentages.length / 100}
        doRound={false}
      />
      <Item title={t.survey.analysis.concept.summary.general.amountWorkstations} value={sums.source.WORKSTATIONS.amount} />
      <Item title={t.survey.analysis.concept.summary.general.amountWorkplaces} value={sums.arbeitsplaetze.sum} />
    </div>
  )
}

export default General
